/**
 * Выбор материала стены, свёрнутый в одну строку: образец штриховки и название выбранного.
 * Материал меняют редко, а список из семи строк занимал пол-панели — теперь он раскрывается
 * по клику и сворачивается, как только материал выбран.
 */
import { useState } from "react";
import { CATALOG, type WallCatalogItem, type WallMaterial } from "@/shared/catalog";
import { UIKit } from "@/shared/ui";
import { MaterialSwatch } from "./MaterialSwatch";

interface Props {
	value: WallMaterial;
	onPick: (material: WallCatalogItem) => void;
	/** Образцы в цвете несущей стены */
	bearing?: boolean;
}

export const WallMaterialPicker = ({ value, onPick, bearing = false }: Props) => {
	const [open, setOpen] = useState(false);
	const items = CATALOG.wall.items;
	const current = items.find((item) => item.material === value) ?? items[0];

	return (
		<div className="flex flex-col gap-0.5">
			<UIKit.Button
				type="button"
				aria-expanded={open}
				title={open ? "Свернуть список материалов" : "Сменить материал"}
				className={`flex items-center gap-2 rounded-lg border bg-surface px-2.5 py-1.5 text-left text-sm text-ink${
					open ? " border-accent" : " border-border hover:border-accent/50"
				}`}
				onClick={() => setOpen(!open)}
			>
				{current && <MaterialSwatch material={current} bearing={bearing} />}
				<UIKit.Text as="span" className="min-w-0 flex-1 truncate">
					{current?.name}
				</UIKit.Text>
				<svg
					width="12"
					height="12"
					viewBox="0 0 12 12"
					aria-hidden
					className={`shrink-0 text-ink/60 transition-transform duration-150${open ? " rotate-180" : ""}`}
				>
					<path
						d="M2.5 4.5 6 8l3.5-3.5"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.6"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</UIKit.Button>
			{open && (
				<div className="flex flex-col gap-0.5" role="listbox" aria-label="Материал стены">
					{items.map((item) => (
						<UIKit.Button
							key={item.id}
							type="button"
							role="option"
							aria-selected={item.material === value}
							className={`flex items-center gap-2 rounded-md border border-transparent px-2.5 py-1.5 text-left text-xs text-ink hover:bg-bg${
								item.material === value
									? " border-accent bg-accent-soft font-medium text-accent"
									: ""
							}`}
							onClick={() => {
								onPick(item);
								setOpen(false);
							}}
						>
							<MaterialSwatch material={item} bearing={bearing} />
							{item.name}
						</UIKit.Button>
					))}
				</div>
			)}
		</div>
	);
};
